// Corpo de uma resposta da IA em Markdown (react-markdown + remark-gfm: tabelas, listas de tarefas, tachado, autolink).
//
// Segurança (a resposta vem de um modelo, que pode repetir texto de documentos e de terceiros):
//  - sem `rehype-raw`: HTML cru nunca é interpretado, vira texto (o react-markdown troca o nó `raw` por texto);
//  - link só http:, https: e mailto: (lista de permitidos, não de bloqueados); o resto — javascript:, data:, relativo — vira texto;
//  - imagem do Markdown NUNCA carrega (vazaria o IP/uso para o host da imagem): vira link com o texto alternativo.
import { memo, type ReactNode } from 'react';
import ReactMarkdown, { type Components, type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check } from 'lucide-react';
import { CopyButton } from './CopyButton';
import '../styles/markdown.css';

type HNode = { type: string; tagName?: string; value?: string; properties?: Record<string, unknown>; children?: HNode[] };

const SAFE_URL = /^(https?:|mailto:)/i;
const safeUrl = (url: string) => (SAFE_URL.test(url) ? url : '');

const textOf = (n: HNode): string => (n.type === 'text' ? (n.value ?? '') : (n.children ?? []).map(textOf).join(''));

function CodeBlock({ node, children }: { node?: HNode; children?: ReactNode }) {
  const code = node?.children?.find((c) => c.type === 'element' && c.tagName === 'code');
  const cls = code?.properties?.className;
  const lang = (Array.isArray(cls) ? cls.map(String).find((c) => c.startsWith('language-')) : undefined)?.slice(9).slice(0, 24) || 'código';
  const raw = code ? textOf(code) : '';
  return (
    <div className="md-code">
      <div className="md-code-h">
        <span className="md-code-lang">{lang}</span>
        <CopyButton text={raw.replace(/\n$/, '')} label="Copiar código" />
      </div>
      <pre tabIndex={0} role="region" aria-label={`Código ${lang}`}>{children}</pre>
    </div>
  );
}

const components: Components = {
  a: ({ href, children }) => (href ? <a href={href} target="_blank" rel="noopener noreferrer nofollow">{children}</a> : <>{children}</>),
  img: ({ src, alt }) => {
    const name = (alt ?? '').trim();
    return src ? <a className="md-img" href={src} target="_blank" rel="noopener noreferrer nofollow" title={src}>{name || 'imagem'}</a> : <>{name}</>;
  },
  pre: ({ node, children }: { node?: ExtraProps['node']; children?: ReactNode }) => <CodeBlock node={node as HNode | undefined}>{children}</CodeBlock>,
  table: ({ children }) => <div className="md-table" tabIndex={0} role="region" aria-label="Tabela"><table>{children}</table></div>,
  // lista de tarefas (GFM): o <input disabled> do plugin não tem rótulo; vira um marcador com nome acessível
  input: ({ type, checked }) => (type === 'checkbox'
    ? <span className={`md-check${checked ? ' on' : ''}`} role="img" aria-label={checked ? 'Feito' : 'Pendente'}>{checked && <Check />}</span>
    : null),
};

/** Põe o cursor de "escrevendo" no fim do último bloco de texto (não numa linha solta abaixo dele). */
function rehypeCursor() {
  return (tree: HNode) => {
    const mark: HNode = { type: 'element', tagName: 'span', properties: { className: ['md-cursor'], ariaHidden: 'true' }, children: [] };
    let n = tree;
    for (;;) {
      const kids = (n.children ?? []).filter((c) => c.type !== 'text' || (c.value ?? '').trim());
      const last = kids[kids.length - 1];
      if (last?.type === 'element' && last.tagName) {
        if (/^(p|h[1-6]|dt|dd|summary)$/.test(last.tagName)) { (last.children ??= []).push(mark); return; }   // texto corrido: o cursor entra no fim dele
        if (/^(ul|ol|li|blockquote|div|details)$/.test(last.tagName)) { n = last; continue; }              // contêiner: desce até o último item
        (tree.children ??= []).push(mark); return;                                                          // tabela, bloco de código, hr: cursor abaixo
      }
      (n.children ??= []).push(mark); return;                                                                // texto/inline solto no fim do contêiner
    }
  };
}

const REMARK = [remarkGfm];
const REHYPE_CURSOR = [rehypeCursor];
const REHYPE_NONE: never[] = [];

/** Memoizado por `text`/`cursor`: no streaming só a bolha que recebe tokens re-renderiza. */
export const MessageBody = memo(function MessageBody({ text, cursor = false }: { text: string; cursor?: boolean }) {
  return (
    <div className="md">
      <ReactMarkdown remarkPlugins={REMARK} rehypePlugins={cursor ? REHYPE_CURSOR : REHYPE_NONE} components={components} urlTransform={safeUrl}>{text}</ReactMarkdown>
    </div>
  );
});
